import React from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Newspaper, Images, Store, Megaphone, MessageSquare,
  ExternalLink, X, Plus, PawPrint, Users, LogOut, CalendarDays, ClipboardCheck, Mic, Handshake, Camera, Sparkles, AudioLines, Trophy,
} from "lucide-react";
import { MobileTopBar, MobileTabBar } from "./MobileNav";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "reporter", "contributor", "coach"] },
  { to: "/admin/articles", label: "Articles", icon: Newspaper, roles: ["admin", "reporter"] },
  { to: "/admin/galleries", label: "Photo Galleries", icon: Images, roles: ["admin", "reporter"] },
  { to: "/admin/interviews", label: "Interviews", icon: AudioLines, roles: ["admin", "reporter"] },
  { to: "/admin/submit", label: "Submit Post", icon: Mic, roles: ["admin", "reporter", "contributor", "coach"] },
  { to: "/admin/review", label: "Review Queue", icon: ClipboardCheck, roles: ["admin"] },
  { to: "/admin/events", label: "Events", icon: CalendarDays, roles: ["admin"] },
  { to: "/admin/directory", label: "Business Directory", icon: Store, roles: ["admin"] },
  { to: "/admin/ads", label: "Ads & Banners", icon: Megaphone, roles: ["admin"] },
  { to: "/admin/sponsors", label: "Section Sponsors", icon: Handshake, roles: ["admin"] },
  { to: "/admin/promotions", label: "Promotions", icon: Trophy, roles: ["admin"] },
  { to: "/admin/loyalty", label: "Loyalty Club", icon: Sparkles, roles: ["admin"] },
  { to: "/admin/header-photos", label: "Header Photos", icon: Camera, roles: ["admin"] },
  { to: "/admin/social", label: "Social Feed", icon: MessageSquare, roles: ["admin"] },
  { to: "/admin/community", label: "Community", icon: PawPrint, roles: ["admin"] },
  { to: "/admin/staff", label: "Staff Accounts", icon: Users, roles: ["admin"] },
];

const ROLE_LABEL = { admin: "Editor / Admin", reporter: "Reporter", contributor: "Community Voice", coach: "Coach" };

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const nav = NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (n.to === "/admin/articles" && user?.role !== "admin" ? { ...n, label: "My Stories" } : n));
  const current = nav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
  const logout = () => { signOut(); navigate("/admin/login"); };
  return (
    <div className="font-ui flex min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col bg-[var(--navy)] text-white lg:flex">
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-4">
          <img src={LOGO_URL} alt="News-Leader" className="h-9 rounded bg-white/95 p-1 object-contain" />
          <div>
            <p className="text-xs font-bold leading-none">Staff CMS</p>
            <p className="text-[10px] text-white/50">Content Management</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-[var(--gold)] text-white" : "text-white/75 hover:bg-white/10"
                }`
              }
            >
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <Link to="/" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/70 hover:bg-white/10">
            <ExternalLink size={16} /> View Live Site
          </Link>
        </div>
      </aside>

      <div className="flex-1 overflow-x-hidden lg:ml-64">
        <MobileTopBar user={user} title={current?.label || "Admin"} onLogout={logout} />
        <header className="sticky top-0 z-30 hidden items-center justify-between border-b border-slate-200 bg-white px-5 py-3 lg:flex">
          <div>
            <h1 className="text-lg font-bold text-[var(--navy)]">{current?.label || "Admin"}</h1>
            <p className="text-xs text-slate-500">News-Leader newsroom & ad operations</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-[var(--navy)]" data-testid="current-user-name">{user?.name}</p>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--gold)]">{ROLE_LABEL[user?.role] || "Staff"}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </header>
        <main className="overflow-x-hidden p-4 pb-28 sm:p-5 lg:pb-5">
          <Outlet />
        </main>
        <MobileTabBar nav={nav} />
      </div>
    </div>
  );
}

// ---- Shared admin UI ----
export function Toolbar({ title, subtitle, onAdd, addLabel = "New" }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-xl font-bold text-[var(--navy)]">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {onAdd && (
        <button
          onClick={onAdd}
          data-testid="admin-add-btn"
          className="flex items-center gap-2 rounded-lg bg-[var(--gold)] px-4 py-2 text-sm font-semibold text-white hover:bg-[#b08f48]"
        >
          <Plus size={16} /> {addLabel}
        </button>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children, onSubmit, busy, submitLabel = "Save" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 sm:p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="admin-modal"
        className="min-h-full w-full max-w-2xl bg-white shadow-2xl sm:my-8 sm:min-h-0 sm:rounded-xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4 sm:static sm:border-0 sm:px-6 sm:pb-0 sm:pt-6">
          <h3 className="text-xl font-bold text-[var(--navy)]">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>
        <div className="space-y-4 px-5 py-4 sm:px-6">{children}</div>
        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3 sm:static sm:border-0 sm:px-6 sm:pb-6 sm:pt-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 sm:flex-none sm:py-2">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="admin-modal-submit" className="flex-1 rounded-lg bg-[var(--navy)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--header-navy)] disabled:opacity-60 sm:flex-none sm:py-2">
            {busy ? "Saving…" : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

export function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

export const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[var(--gold)] bg-white";

export function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
      <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="h-4 w-4 accent-[var(--gold)]" />
      {label}
    </label>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div {...rest} className={`rounded-xl border border-slate-200 bg-white ${className}`}>{children}</div>;
}
